import React, { useState } from 'react'

import ChartBarIcon from '@heroicons/react/outline/ChartBarIcon'
import KeyIcon from '@heroicons/react/outline/KeyIcon'
import LockClosedIcon from '@heroicons/react/outline/LockClosedIcon'
import LockOpenIcon from '@heroicons/react/outline/LockOpenIcon'
import MailIcon from '@heroicons/react/outline/MailIcon'
import PencilAltIcon from '@heroicons/react/outline/PencilAltIcon'
import ShareIcon from '@heroicons/react/outline/ShareIcon'
import ShieldCheckIcon from '@heroicons/react/outline/ShieldCheckIcon'
import ShieldExclamationIcon from '@heroicons/react/outline/ShieldExclamationIcon'
import TrashIcon from '@heroicons/react/outline/TrashIcon'
import UserIcon from '@heroicons/react/outline/UserIcon'
import loadable from '@loadable/component'
import { useLocation } from 'react-router'
import { Link as RouterLink } from 'react-router-dom'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import {
  Member,
  MemberEmailStatus,
  MemberStatus,
  NetworkVisibility,
  RoleType,
} from '@tribeplatform/gql-client/types'
import { MemberAnalyticsModal } from '@tribeplatform/react-components/Analytics/MemberAnalytics'
import { AuditLogsModal } from '@tribeplatform/react-components/AuditLog'
import { ShareModal } from '@tribeplatform/react-components/ShareModal'
import {
  useAuthToken,
  useDeleteMember,
  useNetworkRoles,
  useSendResetPasswordEmail,
  useSuspendMember,
  useUnsuspendMember,
  useVerifyMember,
} from '@tribeplatform/react-sdk/hooks'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { ProfileSettingsModal } from '../Settings/ProfileSettingsModal'

const ManageBadgesModal = loadable(
  () => import('../Admin/MemberBadges/ManageBadgesModal'),
  {
    resolveComponent: module => module.ManageBadgesModal,
  },
)

type MemberActionItemsProps = {
  member: Member
  className?: string
}

export const MemberActionItems = ({
  member,
  className,
}: MemberActionItemsProps) => {
  const [shareModal, setShareModal] = useState<boolean>(false)
  const [editModal, setEditModal] = useState(false)
  const [auditLogsModal, setAuditLogsModal] = useState<boolean>(false)
  const [assignBadgesModal, setAssignBadgesModal] = useState<boolean>(false)
  const [memberAnalyticsModal, setMemberAnalyticsModal] =
    useState<boolean>(false)

  const {
    data: { network, member: authMember },
  } = useAuthToken()

  const { pathname } = useLocation()

  const roles = useNetworkRoles()

  const { mutateAsync: sendResetPasswordEmail } = useSendResetPasswordEmail()
  const { mutateAsync: verifyEmail } = useVerifyMember()
  const { mutateAsync: deleteMember } = useDeleteMember()
  const { mutateAsync: suspendMember } = useSuspendMember()
  const { mutateAsync: unsuspendMember } = useUnsuspendMember()

  const [
    canUpdateMember,
    canSuspendMember,
    canUnsuspendMember,
    canRemoveMember,
  ] = hasScopesPermission(member, [
    'updateMember',
    'suspendMember',
    'unsuspendMember',
    'removeMember',
  ])

  const [
    canUpdateNetwork,
    canAssignBadge,
    canSendResetPasswordEmail,
    canVerifyMember,
  ] = hasScopesPermission(network, [
    'updateNetwork',
    'assignBadge',
    'sendResetPasswordEmail',
    'verifyMember',
  ])

  const memberPath = member?.relativeUrl

  const isMemberPage = pathname === memberPath
  const isOwnProfile = authMember?.id === member?.id

  const onDelete = async (member: Member) => {
    if (!member?.id) return

    const confirmed = await confirm({
      title: `Are you sure you want to remove ${member?.name} (${member?.username})?`,
      proceedLabel: 'Remove',
      danger: true,
    })
    if (!confirmed) {
      return
    }

    try {
      await deleteMember({ id: member?.id })
      toast({
        title: 'Member removed',
        description: 'Member is successfully removed.',
        status: 'success',
      })
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Cannot remove this member.',
        status: 'error',
      })
    }
  }

  const onSuspend = async (member: Member) => {
    if (!member?.id) return

    const confirmed = await confirm({
      title: `Member suspension`,
      description:
        'Suspending a member will take away their ability to login and engage with your community as a registered member.' +
        ' Please note that you will be able to revoke the suspension if you will choose to do so later on.',
      proceedLabel: 'Suspend',
    })
    if (!confirmed) {
      return
    }

    try {
      await suspendMember({ memberId: member?.id })
      toast({
        title: 'Member suspended',
        description: 'Member is successfully suspended.',
        status: 'success',
      })
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Cannot suspend this member.',
        status: 'error',
      })
    }
  }

  const onUnsuspend = async (member: Member) => {
    if (!member?.id) return

    try {
      await unsuspendMember({ memberId: member?.id })
      toast({
        title: 'Member suspension revoked',
        description: 'Member suspension is successfully revoked.',
        status: 'success',
      })
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Cannot revoke suspension for this member.',
        status: 'error',
      })
    }
  }

  const onSendResetPasswordEmail = async (member: Member) => {
    if (!member?.id) return

    const confirmed = await confirm({
      title: `Reset password`,
      description:
        "A reset password link will be sent to member's email address. Are you sure you want to continue?",
      proceedLabel: 'Email reset password link',
    })
    if (!confirmed) {
      return
    }

    try {
      await sendResetPasswordEmail({ email: member?.email })
      toast({
        title: 'Email sent',
        description: 'Reset password email sent to the member.',
        status: 'success',
      })
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Cannot send reset password email.',
        status: 'error',
      })
    }
  }

  const onVerifyEmail = async memberId => {
    const confirmed = await confirm({
      title:
        "Are you sure you want to manually confirm member's email address?",
      description:
        'Confirming incorrect email addresses can hurt your email delivery rate.',
      proceedLabel: 'Yes',
      cancelLabel: 'No',
    })
    if (!confirmed) {
      return
    }

    await verifyEmail({
      input: {
        memberId,
      },
    })

    toast({
      title: 'Email address verified',
      description: 'Email address was manually marked as verified.',
      status: 'success',
    })
  }

  return (
    <>
      <Dropdown.Items className={className}>
        {!isMemberPage && (
          <Dropdown.Item
            as={RouterLink}
            to={memberPath}
            leadingIcon={<UserIcon />}
          >
            View profile
          </Dropdown.Item>
        )}
        {canUpdateMember && !isMemberPage && (
          <Dropdown.Item
            leadingIcon={<PencilAltIcon />}
            onClick={() => {
              setEditModal(true)
            }}
          >
            Edit profile
          </Dropdown.Item>
        )}
        <Dropdown.Item
          leadingIcon={<ShareIcon />}
          onClick={() => {
            setShareModal(true)
          }}
        >
          Share
        </Dropdown.Item>
        {canAssignBadge && (
          <Dropdown.Item
            leadingIcon={<ShieldCheckIcon />}
            onClick={() => setAssignBadgesModal(true)}
          >
            Manage badges
          </Dropdown.Item>
        )}
        {canUpdateNetwork && (
          <Dropdown.Item
            leadingIcon={<ChartBarIcon />}
            onClick={() => setMemberAnalyticsModal(true)}
          >
            Analytics
          </Dropdown.Item>
        )}
        {canUpdateNetwork && (
          <Dropdown.Item
            leadingIcon={<ShieldExclamationIcon />}
            onClick={() => setAuditLogsModal(true)}
          >
            Audit logs
          </Dropdown.Item>
        )}
        {authMember?.role.type === RoleType.admin && canSendResetPasswordEmail && (
          <Dropdown.Item
            leadingIcon={<KeyIcon />}
            onClick={() => onSendResetPasswordEmail(member)}
          >
            Reset password
          </Dropdown.Item>
        )}
        {!isOwnProfile &&
          authMember?.role.type === RoleType.admin &&
          member.emailStatus !== MemberEmailStatus.verified &&
          canVerifyMember && (
            <Dropdown.Item
              leadingIcon={<MailIcon />}
              onClick={() => onVerifyEmail(member?.id)}
            >
              Verify email address
            </Dropdown.Item>
          )}
        {member?.['role.name'] !== roles?.admin?.name &&
          member?.['role.name'] !== roles?.moderator?.name &&
          canSuspendMember &&
          !isOwnProfile &&
          member?.status !== MemberStatus.Suspended && (
            <Dropdown.Item
              leadingIcon={<LockClosedIcon />}
              onClick={() => onSuspend(member)}
            >
              Suspend member
            </Dropdown.Item>
          )}
        {canUnsuspendMember &&
          member?.status === MemberStatus.Suspended &&
          !isOwnProfile && (
            <Dropdown.Item
              leadingIcon={<LockOpenIcon />}
              onClick={() => onUnsuspend(member)}
            >
              Revoke suspension
            </Dropdown.Item>
          )}
        {canRemoveMember && !isOwnProfile && (
          <Dropdown.Item
            leadingIcon={<TrashIcon />}
            onClick={() => onDelete(member)}
          >
            Remove member
          </Dropdown.Item>
        )}
      </Dropdown.Items>

      <ShareModal
        isPrivate={network.visibility === NetworkVisibility.private}
        memberId={member?.id}
        open={shareModal}
        onClose={() => setShareModal(false)}
        title={member?.name}
        url={`https://${network?.domain}${memberPath}`}
      />
      <ProfileSettingsModal
        open={editModal}
        onClose={() => {
          setEditModal(false)
        }}
        member={member}
      />
      {editModal ? (
        <ProfileSettingsModal
          member={member}
          open
          onClose={() => setEditModal(false)}
        />
      ) : null}
      {auditLogsModal && (
        <AuditLogsModal
          open
          onClose={() => setAuditLogsModal(false)}
          memberId={member?.id}
        />
      )}
      {assignBadgesModal && (
        <ManageBadgesModal
          open
          onClose={() => setAssignBadgesModal(false)}
          memberId={member?.id}
        />
      )}
      {memberAnalyticsModal && (
        <MemberAnalyticsModal
          open
          onClose={() => setMemberAnalyticsModal(false)}
          member={member}
          linkToContent={false}
        />
      )}
    </>
  )
}
